// -----------------------------------------------------------------------------
// TexasLLC.jsx — canonical page for /texas-llc-formation-attorney/
//
// Texas LLC Formation and Operating Agreements. Attorney-guided alternative
// to the DIY route (LaunchMyEntity.com is referenced as a sibling resource
// but is NOT linked as a purchase destination until its service and checkout
// are independently verified — see implementation brief).
// -----------------------------------------------------------------------------
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import AttorneyReviewNote from "@/components/AttorneyReviewNote";
import { breadcrumbSchema, faqPageSchema, legalServiceSchema, attorneySchema } from "@/lib/schemas";
import { trackAuthorityPageView, trackPriorityFaqOpen } from "@/lib/analytics";
import { ChevronDown, Phone, Star } from "lucide-react";

const PHONE = "(972) 712-1515";
const PHONE_TEL = "9727121515";
const CANONICAL = "/texas-llc-formation-attorney/";

const IN_PAGE_NAV = [
  ["What's Included", "included"],
  ["Operating Agreement", "operating-agreement"],
  ["DIY vs Attorney", "diy-vs-attorney"],
  ["FAQs", "faqs"],
  ["Related", "related"],
  ["Contact", "contact"],
];

// The actual scope of attorney-guided LLC formation — what the client gets.
const SCOPE_ITEMS = [
  ["Entity selection review", "LLC, Series LLC, PLLC, LP, or corporation — matched to the business, ownership, tax posture, and intended operations before filing."],
  ["Texas Certificate of Formation", "Filed with the Secretary of State under the Texas Business Organizations Code, with the registered-agent and management-structure elections suited to the business."],
  ["EIN and tax-election coordination", "Federal Employer Identification Number application, S-corporation election review, and coordinated communication with the client's CPA on initial tax posture."],
  ["Operating or company agreement", "The internal rulebook — management, voting, capital, distributions, transfer restrictions, buy-sell triggers, death and disability provisions, and dispute resolution — drafted for the owners actually involved, not a boilerplate placeholder."],
  ["Member certificates and ledger", "Opening membership interest ledger, admission documentation, and — where appropriate — written consents memorializing the organizational meeting."],
  ["Beneficial-ownership guidance", "Current posture on federal beneficial-ownership reporting (FinCEN) and Texas franchise-tax registration, with clear next-step instructions for the owners."],
  ["Registered agent", "Firm-provided registered agent service is available for Texas entities on request."],
  ["Coordination with the plan", "The LLC is drafted in coordination with the owners' estate plan, buy-sell agreement, and asset-protection posture — not in isolation from them."],
];

// Operating-agreement provisions that matter — in plain English.
const OPERATING_AGREEMENT_POINTS = [
  ["Management", "Member-managed vs manager-managed; the authority of each class; what requires unanimous consent and what does not."],
  ["Capital contributions", "Initial capital, additional contributions, capital-call mechanics, and the consequences of a member who fails to fund a call."],
  ["Distributions and allocations", "Priority of distributions, timing, tax-distribution floors, and allocations of profit and loss for federal income-tax purposes."],
  ["Transfer restrictions", "The rules on who may receive a member's interest — by sale, by gift, at death, in divorce, on bankruptcy — and how those transfers are priced and funded."],
  ["Buy-sell triggers", "Mandatory and permissive buy-outs on death, disability, retirement, deadlock, disagreement, and departure — plus the valuation method and payment terms."],
  ["Deadlock and dissociation", "The procedure when owners disagree on a fundamental decision; the procedure when an owner leaves."],
  ["Dispute resolution", "Negotiation, mediation, and venue provisions — before any dispute is live."],
  ["Amendment thresholds", "What percentage of owners must agree to amend each class of provision; higher thresholds for foundational terms, simpler thresholds for operational ones."],
];

// Priority FAQs — the four questions most owners actually ask.
const PRIORITY_FAQ = [
  [
    "Do I need an attorney to form a Texas LLC?",
    "An LLC may be formed with the Texas Secretary of State without an attorney. An attorney becomes important when the business has more than one owner, operates in a regulated field (medical, financial, licensed-professional), expects outside capital, holds real estate, employs people, or will be the subject of a buy-sell agreement or estate-plan integration. For a straightforward one-owner Texas LLC, LaunchMyEntity.com is the firm's DIY formation route. For complex family, business, or tax planning — call the firm.",
  ],
  [
    "What is a Texas operating agreement, and is one required?",
    "The operating agreement — formally the company agreement under the Texas Business Organizations Code — is the LLC's internal rulebook. Texas does not file the agreement with the State and does not strictly require one to form the entity. In practice, every Texas LLC should have a written agreement: without it, the default statutory rules apply, and those defaults are rarely what the owners actually want.",
  ],
  [
    "Should I file as an S-corporation?",
    "The S-election is a federal income-tax posture; the LLC remains an LLC under Texas law. An S-election may be appropriate when the owner draws a reasonable salary from the business and the remaining profits exceed a certain threshold. The decision is tax-driven and should be made with a CPA — we coordinate with the client's accountant before filing Form 2553.",
  ],
  [
    "What is a Series LLC, and when should I consider one?",
    "A Series LLC under Texas Business Organizations Code § 101.601 et seq. permits multiple protected cells inside one legal entity, with internal liability shields between series. It can be powerful for real-estate owners holding multiple properties, but the operational discipline — separate books, separate accounts, careful contracting in the name of the correct series — is non-negotiable. A Series LLC that is not operated correctly is weaker than two ordinary LLCs operated correctly.",
  ],
];

// Related practice areas on-site.
const RELATED = [
  ["Business Succession Planning", "/texas-business-succession-planning-attorney/", "Buy-sell agreements, management transitions, and ownership transfers for Texas businesses."],
  ["Asset Purchase Agreements", "/texas-asset-purchase-agreement-attorney/", "Buying or selling a Texas business — structure, LOI, diligence, definitive, and close."],
  ["Business-Owner Trust Planning", "/trusts/business-owner-trust-planning", "Trust ownership of business interests, coordinated with the buy-sell agreement and the estate plan."],
  ["Trusts & Estate Planning", "/trusts", "Texas trust planning, business succession, asset protection, and fiduciary counsel."],
];

export default function TexasLLC() {
  useEffect(() => {
    trackAuthorityPageView({
      page_slug: "texas-llc-formation-attorney",
      page_title: "Texas LLC Formation and Operating Agreements",
      priority_faq_count: PRIORITY_FAQ.length,
    });
  }, []);

  const [openFaq, setOpenFaq] = useState(null);
  const handleFaqToggle = (idx, question) => {
    const wasOpen = openFaq === idx;
    setOpenFaq(wasOpen ? null : idx);
    if (!wasOpen) {
      trackPriorityFaqOpen({
        page_slug: "texas-llc-formation-attorney",
        faq_position: idx + 1,
        faq_question: question,
      });
    }
  };

  return (
    <div className="bg-ivory" data-testid="texas-llc-page">
      <SEO
        title="Texas LLC Formation and Operating Agreements | Lone Star Counsel"
        description="Attorney-guided Texas LLC formation and operating agreements for owners who need more than a filing. Entity selection, company agreement, buy-sell coordination, and estate-plan integration. Call (972) 712-1515."
        path={CANONICAL}
        schema={[
          legalServiceSchema({
            name: "Texas LLC Formation and Operating Agreements",
            description: "Attorney-guided Texas LLC formation and operating agreement drafting, coordinated with buy-sell and estate planning.",
            url: CANONICAL,
            area: "Texas",
          }),
          attorneySchema(),
          faqPageSchema(PRIORITY_FAQ.map(([q, a]) => ({ q, a }))),
          breadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Practice", url: "/practice" },
            { name: "Texas LLC Formation", url: CANONICAL },
          ]),
        ]}
      />

      {/* HERO */}
      <section className="bg-ivory" data-testid="llc-hero">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 pt-32 md:pt-40 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="kicker">Lone Star &#9733; Counsel</div>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-navy" data-testid="llc-h1">
              Texas LLC Formation<br/>
              <span className="italic text-oxblood">and Operating Agreements.</span>
            </h1>
            <p className="mt-8 max-w-2xl text-lg md:text-xl text-slate2 font-serif leading-relaxed" data-testid="llc-direct-answer">
              The filing is one part of forming a business. Lone Star Counsel helps owners address management, ownership, operating agreements, and the relationship between the business and personal planning. For straightforward one-owner formations, LaunchMyEntity.com is the firm&rsquo;s DIY route. For multi-owner, regulated, real-estate, or estate-coordinated matters &mdash; begin with an attorney conversation.
            </p>
            <AttorneyReviewNote attorney="Darryl V. Pratt, Esq., CPA" date="February 2026" />
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/intake" className="btn-primary" data-testid="llc-hero-primary">Request an LLC Consultation</Link>
              <a href={`tel:${PHONE_TEL}`} className="btn-ghost" data-testid="llc-hero-secondary">
                <Phone size={16} className="inline mr-2" />Call {PHONE} for attorney-guided LLC services
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img
                src="/img/practice-succession.jpg"
                alt="Attorney counsel for Texas LLC formation and operating agreements."
                className="w-full h-full object-cover"
                loading="eager"
              />
            </div>
          </div>
        </div>
      </section>

      {/* IN-PAGE NAV */}
      <nav className="bg-ivory border-y border-navy/10 sticky top-16 z-30" data-testid="llc-inpage-nav">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-3 flex flex-wrap gap-x-8 gap-y-2 text-[11px] uppercase tracking-[0.26em] text-slate2">
          {IN_PAGE_NAV.map(([label, anchor]) => (
            <a key={anchor} href={`#${anchor}`} className="hover:text-oxblood transition" data-testid={`llc-nav-${anchor}`}>{label}</a>
          ))}
        </div>
      </nav>

      {/* WHAT'S INCLUDED */}
      <section id="included" className="bg-ivory py-20 md:py-28" data-testid="llc-included">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="kicker">What&rsquo;s Included</div>
            <h2 className="mt-6 font-display text-3xl md:text-5xl leading-[1.0] text-navy">
              Attorney-guided Texas LLC formation.
            </h2>
            <p className="mt-6 text-slate2 font-serif text-lg leading-relaxed">
              Entity selection, filing, governance, and coordination with the business and personal planning the client already has in place &mdash; or will soon.
            </p>
          </div>
          <div className="lg:col-span-7">
            <dl className="space-y-6" data-testid="llc-scope-list">
              {SCOPE_ITEMS.map(([title, body], i) => (
                <div key={i} className="hairline-top pt-5" data-testid={`llc-scope-${i}`}>
                  <dt className="font-display text-xl md:text-2xl text-navy flex items-center gap-3">
                    <Star size={14} className="text-brass" aria-hidden="true" />
                    {title}
                  </dt>
                  <dd className="mt-2 text-slate2 font-serif leading-relaxed">{body}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      {/* OPERATING AGREEMENT */}
      <section id="operating-agreement" className="bg-linen py-20 md:py-28" data-testid="llc-operating-agreement">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="kicker">The Company Agreement</div>
            <h2 className="mt-6 font-display text-3xl md:text-5xl leading-[1.0] text-navy">
              The internal rulebook.
            </h2>
            <p className="mt-6 text-slate2 font-serif text-lg leading-relaxed">
              Texas does not require filing a company agreement with the State. In practice, every multi-owner LLC should have one &mdash; and the drafting choices matter the first time an owner leaves, dies, divorces, or disagrees.
            </p>
          </div>
          <div className="lg:col-span-7">
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6" data-testid="llc-oa-points">
              {OPERATING_AGREEMENT_POINTS.map(([title, body], i) => (
                <li key={i} className="hairline-top pt-4" data-testid={`llc-oa-${i}`}>
                  <div className="font-display text-lg md:text-xl text-navy">{title}</div>
                  <div className="mt-2 text-slate2 font-serif text-sm leading-relaxed">{body}</div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* DIY vs ATTORNEY */}
      <section id="diy-vs-attorney" className="bg-ivory py-20 md:py-28" data-testid="llc-diy-vs-attorney">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="kicker">Two Routes</div>
            <h2 className="mt-6 font-display text-3xl md:text-5xl leading-[1.0] text-navy">
              DIY formation, or attorney-guided?
            </h2>
            <p className="mt-6 text-slate2 font-serif text-lg leading-relaxed">
              Not every Texas LLC needs an attorney. The decision depends on the owners, the business, and the planning around it.
            </p>
          </div>
          <div className="lg:col-span-7 space-y-8">
            <div className="hairline-top pt-6" data-testid="llc-diy-card">
              <div className="kicker">DIY Formation</div>
              <h3 className="mt-3 font-display text-xl md:text-2xl text-navy">LaunchMyEntity.com &mdash; the self-service route</h3>
              <p className="mt-3 text-slate2 font-serif text-sm leading-relaxed">
                For a single-owner Texas LLC where the owner understands the trade-offs and the business is straightforward, the DIY route is sensible and inexpensive. LaunchMyEntity.com is the firm&rsquo;s sibling resource for self-service Texas entity formation. We will publish a direct purchase link only after that service&rsquo;s checkout and fulfillment are independently verified.
              </p>
            </div>
            <div className="hairline-top pt-6" data-testid="llc-attorney-card">
              <div className="kicker">Attorney-Guided</div>
              <h3 className="mt-3 font-display text-xl md:text-2xl text-navy">Lone Star &#9733; Counsel &mdash; multi-owner, regulated, or coordinated</h3>
              <p className="mt-3 text-slate2 font-serif text-sm leading-relaxed">
                Begin with an attorney when the LLC will have more than one owner, operate in a regulated profession, hold real estate, employ people, be the subject of a buy-sell agreement, be owned by a trust, or need to coordinate with the owners&rsquo; estate plan. The upfront cost is modest compared to the first dispute, divorce, or departure the agreement will govern.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Link to="/intake" className="btn-primary text-sm" data-testid="llc-attorney-cta">Request an LLC Consultation</Link>
                <a href={`tel:${PHONE_TEL}`} className="btn-ghost text-sm" data-testid="llc-attorney-call">
                  <Phone size={14} className="inline mr-2" />{PHONE}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section id="faqs" className="bg-linen py-20 md:py-28" data-testid="llc-faqs">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="kicker">Frequently Asked</div>
          <h2 className="mt-6 font-display text-3xl md:text-5xl leading-[1.0] text-navy">
            Texas LLC questions.
          </h2>
          <ul className="mt-12 space-y-6" data-testid="llc-faq-list">
            {PRIORITY_FAQ.map(([q, a], i) => (
              <li key={i} className="hairline-top pt-5" data-testid={`llc-faq-${i}`}>
                <button
                  className="w-full flex items-start justify-between gap-6 text-left group"
                  onClick={() => handleFaqToggle(i, q)}
                  aria-expanded={openFaq === i}
                  data-testid={`llc-faq-toggle-${i}`}
                >
                  <span className="font-display text-lg md:text-2xl text-navy group-hover:text-oxblood transition">{q}</span>
                  <ChevronDown size={20} className={`text-brass transition-transform mt-1 ${openFaq === i ? "rotate-180" : ""}`} aria-hidden="true" />
                </button>
                {openFaq === i && (
                  <p className="mt-4 text-slate2 font-serif leading-relaxed" data-testid={`llc-faq-answer-${i}`}>{a}</p>
                )}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* RELATED */}
      <section id="related" className="bg-ivory py-20 md:py-28" data-testid="llc-related">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="kicker">Also at the Firm</div>
          <h2 className="mt-6 font-display text-3xl md:text-5xl leading-[1.0] text-navy">
            Related Texas practice areas.
          </h2>
          <ul className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8" data-testid="llc-related-list">
            {RELATED.map(([label, href, desc], i) => (
              <li key={i} className="hairline-top pt-5" data-testid={`llc-related-${i}`}>
                <Link to={href} className="group block">
                  <div className="font-display text-xl md:text-2xl text-navy group-hover:text-oxblood transition">{label}</div>
                  <div className="mt-2 text-slate2 font-serif text-sm leading-relaxed">{desc}</div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="panel-navy py-20 md:py-28" data-testid="llc-contact">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 text-center">
          <div className="kicker">Lone Star &#9733; Counsel</div>
          <h2 className="mt-6 font-display text-3xl md:text-5xl text-ivory leading-[1.0]">
            Begin a Texas LLC conversation.
          </h2>
          <p className="mt-6 max-w-2xl mx-auto text-ivory/80 font-serif text-lg leading-relaxed">
            Attorney-guided LLC formation, operating-agreement drafting, and coordination with the buy-sell, trust, and estate plan the owners already have &mdash; or will soon.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link to="/intake" className="btn-brass" data-testid="llc-contact-primary">Request an LLC Consultation</Link>
            <a href={`tel:${PHONE_TEL}`} className="btn-ghost-light" data-testid="llc-contact-call">
              <Phone size={16} className="inline mr-2" />Call {PHONE}
            </a>
          </div>
          <p className="mt-6 text-ivory/60 font-serif text-sm">LONE STAR &#9733; COUNSEL is a dba of Pratt Law Group, PLLC. Nearly 30 years of Texas business, tax, and succession counsel.</p>
        </div>
      </section>
    </div>
  );
}
